import { apiClient } from '../../apiHelper';
import { useCreate, useEdit, useSingleFetch } from '../react-query';
import mobileLicensingQueryKeys from './mobile-licensing-query-keys';

export interface MobileLicense {
  id: string;
  organization_id?: string | null;
  label: string;
  license_key: string;
  license_type: string;
  status: string;
  seat_limit: number;
  assigned_seats: number;
  expires_at?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface MobileLicenseCreateInput {
  label: string;
  license_type?: string;
  status?: string;
  seat_limit: number;
  expires_at?: string | null;
  notes?: string | null;
}

export interface MobileLicenseUpdateInput {
  id: string;
  label?: string;
  license_type?: string;
  status?: string;
  seat_limit?: number;
  expires_at?: string | null;
  notes?: string | null;
}

export interface MobileEnrolledDevice {
  id: string;
  organization_id: string;
  license_id?: string | null;
  device_label: string;
  device_fingerprint: string;
  installation_id?: string | null;
  app_version?: string | null;
  status: string;
  last_seen_at?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface MobileManifestRelease {
  id: string;
  organization_id: string;
  label: string;
  manifest_version: string;
  status: string;
  artifact_count: number;
  is_active: boolean;
  published_at?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface MobileManifestArtifact {
  id: string;
  organization_id: string;
  manifest_release_id: string;
  model_key: string;
  model_version: string;
  source_uri: string;
  source_hash_sha256?: string | null;
  source_size_bytes?: number | null;
  pack_format: string;
  sort_order: number;
  is_required: boolean;
  is_enabled: boolean;
  notes?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface MobileManifestArtifactInput {
  id?: string;
  model_key: string;
  model_version: string;
  source_uri: string;
  source_hash_sha256?: string | null;
  source_size_bytes?: number | null;
  pack_format?: string;
  sort_order?: number;
  is_required?: boolean;
  is_enabled?: boolean;
  notes?: string | null;
}

export interface MobileManifestReleaseCreateInput {
  label: string;
  manifest_version: string;
  status?: string;
  artifact_count: number;
  is_active?: boolean;
  published_at?: string | null;
  notes?: string | null;
  artifacts?: MobileManifestArtifactInput[];
}

export interface MobileManifestReleaseUpdateInput {
  id: string;
  label?: string;
  manifest_version?: string;
  status?: string;
  artifact_count?: number;
  is_active?: boolean;
  published_at?: string | null;
  notes?: string | null;
  artifacts?: MobileManifestArtifactInput[];
}

export interface MobileLicensingSummary {
  license_count: number;
  active_license_count: number;
  total_seats: number;
  assigned_seats: number;
  device_count: number;
  active_device_count: number;
  active_manifest_version?: string | null;
}

export interface MobileLicensingOverview {
  organization_id?: string | null;
  organization_name?: string | null;
  summary: MobileLicensingSummary;
  licenses: MobileLicense[];
  devices: MobileEnrolledDevice[];
  manifests: MobileManifestRelease[];
}

export interface MobileDeviceUpdateInput {
  id: string;
  device_label?: string;
  app_version?: string | null;
  status?: string;
  license_id?: string | null;
}

export function useMobileLicensingOverview(config = {}) {
  const getMobileLicensingOverviewFn = async (): Promise<MobileLicensingOverview> => {
    const response = await apiClient.get('organization/mobile-licensing/');
    return response.data || response;
  };

  return useSingleFetch(
    getMobileLicensingOverviewFn,
    mobileLicensingQueryKeys.detail(),
    'mobile-licensing',
    config
  );
}

export function useCreateMobileLicense(
  onSuccess?: (response: unknown, data: MobileLicenseCreateInput) => void,
  onError?: (response: unknown, error: Error) => void,
  notification = true
) {
  const createMobileLicenseFn = async (data: MobileLicenseCreateInput) => {
    const response = await apiClient.post('organization/mobile-licensing/licenses', data);
    return response;
  };

  return useCreate(
    createMobileLicenseFn,
    [...mobileLicensingQueryKeys.all.map(String)],
    'Error while creating mobile license',
    'Mobile license created successfully',
    onSuccess,
    onError,
    notification
  );
}

export function useUpdateMobileLicense(
  onSuccess?: (response: unknown, data: MobileLicenseUpdateInput) => void,
  onError?: (response: unknown, error: Error) => void,
  notification = true
) {
  const updateMobileLicenseFn = async ({ id, ...data }: MobileLicenseUpdateInput) => {
    const response = await apiClient.put(`organization/mobile-licensing/licenses/${id}`, data);
    return response;
  };

  return useEdit(
    updateMobileLicenseFn,
    [...mobileLicensingQueryKeys.all.map(String)],
    'Error while updating mobile license',
    'Mobile license updated successfully',
    onSuccess,
    onError,
    notification
  );
}

export function useCreateMobileManifestRelease(
  onSuccess?: (response: unknown, data: MobileManifestReleaseCreateInput) => void,
  onError?: (response: unknown, error: Error) => void,
  notification = true
) {
  const createMobileManifestReleaseFn = async (data: MobileManifestReleaseCreateInput) => {
    const response = await apiClient.post('organization/mobile-licensing/manifests', data);
    return response;
  };

  return useCreate(
    createMobileManifestReleaseFn,
    [...mobileLicensingQueryKeys.all.map(String)],
    'Error while creating mobile release',
    'Mobile release created successfully',
    onSuccess,
    onError,
    notification
  );
}

export async function listMobileManifestArtifacts(
  manifestId: string
): Promise<MobileManifestArtifact[]> {
  const response = await apiClient.get(
    `organization/mobile-licensing/manifests/${manifestId}/artifacts`
  );
  return response.data || response;
}

export function useUpdateMobileManifestRelease(
  onSuccess?: (response: unknown, data: MobileManifestReleaseUpdateInput) => void,
  onError?: (response: unknown, error: Error) => void,
  notification = true
) {
  const updateMobileManifestReleaseFn = async ({
    id,
    ...data
  }: MobileManifestReleaseUpdateInput) => {
    const response = await apiClient.put(`organization/mobile-licensing/manifests/${id}`, data);
    return response;
  };

  return useEdit(
    updateMobileManifestReleaseFn,
    [...mobileLicensingQueryKeys.all.map(String)],
    'Error while updating mobile release',
    'Mobile release updated successfully',
    onSuccess,
    onError,
    notification
  );
}

export function useUpdateMobileDevice(
  onSuccess?: (response: unknown, data: MobileDeviceUpdateInput) => void,
  onError?: (response: unknown, error: Error) => void,
  notification = true
) {
  const updateMobileDeviceFn = async ({ id, ...data }: MobileDeviceUpdateInput) => {
    const response = await apiClient.put(`organization/mobile-licensing/devices/${id}`, data);
    return response;
  };

  return useEdit(
    updateMobileDeviceFn,
    [...mobileLicensingQueryKeys.all.map(String)],
    'Error while updating mobile device',
    'Mobile device updated successfully',
    onSuccess,
    onError,
    notification
  );
}
